<template>
  <div class="comments-container flex flex-col bg-white rounded-xl h-full max-h-[80vh] overflow-hidden font-sans">
    <!-- Notification banner -->
    <transition name="notification">
      <div
        v-if="newReplyNotification"
        class="notification-banner absolute top-4 left-1/2 transform -translate-x-1/2 z-[1001] px-4 py-2 rounded-md text-sm font-medium shadow-lg"
      >
        {{ newReplyNotification }}
      </div>
    </transition>

    <!-- Header -->
    <div class="comments-header px-6 py-5 border-b">
      <div class="header-content flex justify-between items-center">
        <h2 class="header-title m-0 text-lg font-semibold flex items-center gap-2">
          {{ t('canvasUI.comments.title') }}
        </h2>
        <div class="thread-stats flex gap-2">
          <span class="stat-badge text-xs font-bold px-2 py-1 rounded-2xl">{{
            t('canvasUI.comments.threadCount', { count: localThreads.length })
          }}</span>
          <span v-if="resolvedCount > 0" class="stat-badge resolved text-xs font-bold px-2 py-1 rounded-2xl">{{
            t('canvasUI.comments.resolvedCount', { count: resolvedCount })
          }}</span>
          <span
            v-if="isSaving"
            class="stat-badge saving text-xs font-bold px-2 py-1 rounded-2xl inline-flex items-center gap-1"
          >
            <CIcon class="fa-spin" fa-type="fas" icon="spinner" :size="12" />
            {{ t('canvasUI.comments.saving') }}
          </span>
          <span v-else-if="lastSaved" class="stat-badge saved text-xs font-bold px-2 py-1 rounded-2xl">{{
            t('canvasUI.comments.saved')
          }}</span>
        </div>
      </div>
    </div>

    <div class="comments-wrapper flex-1 flex flex-col overflow-hidden">
      <!-- Loading state -->
      <div v-if="isLoading" class="loading-state flex-1 flex flex-col items-center justify-center p-12 text-center">
        <CIcon class="fa-spin loading-spinner mb-4" fa-type="fas" icon="spinner" :size="40" />
        <p class="loading-text m-0 text-base font-medium">{{ t('canvasUI.comments.loading') }}</p>
      </div>

      <!-- Empty state -->
      <div
        v-else-if="localThreads.length === 0"
        class="empty-state flex-1 flex flex-col items-center justify-center p-12 text-center"
      >
        <CIcon class="empty-icon mb-4" icon="comment-slash" :size="48" />
        <p class="empty-text m-0 text-base font-medium">{{ t('canvasUI.comments.noComments') }}</p>
        <p class="empty-subtext m-0 text-sm">{{ t('canvasUI.comments.beFirst') }}</p>
      </div>

      <!-- Thread list -->
      <div v-else class="comments-threads flex-1 p-4 overflow-y-auto scrollbar-thin scrollbar-thumb-slate-300">
        <transition-group name="thread-list">
          <div
            v-for="thread in localThreads"
            :key="thread.id"
            class="comment-thread p-0 mb-4 transition-all duration-200"
            :class="{ 'thread-resolved': thread.resolved }"
          >
            <div class="thread-main flex gap-3">
              <div class="thread-avatar flex-shrink-0">
                <div
                  class="avatar-placeholder w-10 h-10 rounded-full flex items-center justify-center text-sm font-semibold text-white"
                >
                  {{ getInitials(thread.author) }}
                </div>
              </div>

              <div class="thread-body flex-1 min-w-0">
                <div class="thread-meta flex items-center gap-2 mb-0.5">
                  <span class="author-name font-semibold text-sm">{{
                    thread.author || t('canvasUI.comments.anonymous')
                  }}</span>
                  <span class="thread-time text-xs">{{ formatTime(thread.createdAt) }}</span>
                  <span
                    v-if="thread.resolved"
                    class="resolved-badge inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-xl"
                  >
                    <CIcon fa-type="fas" icon="check" :size="12" />
                    {{ t('canvasUI.comments.resolved') }}
                  </span>
                  <span
                    v-if="thread.assignee"
                    class="assignee-badge inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-xl cursor-default"
                    :title="thread.assignee.name"
                  >
                    <CIcon fa-type="fas" icon="user" :size="12" />
                    {{ getInitials(thread.assignee.name) }}
                  </span>
                </div>

                <div class="comment-content text-sm leading-relaxed break-words">{{ thread.content }}</div>

                <!-- Replies -->
                <div v-if="thread.comments.length > 0" class="thread-replies mt-3 ml-7 pl-3 border-l-2">
                  <div v-for="comment in thread.comments" :key="comment.id" class="reply flex gap-2 mb-3 last:mb-0">
                    <div class="reply-avatar flex-shrink-0">
                      <div
                        class="avatar-placeholder small w-6 h-6 rounded-full flex items-center justify-center text-xs font-semibold text-white"
                      >
                        {{ getInitials(comment.author || t('canvasUI.comments.anonymous')) }}
                      </div>
                    </div>
                    <div class="reply-content flex-1">
                      <div class="reply-meta flex items-center gap-1.5 mb-0">
                        <span class="reply-author text-xs font-medium">{{
                          comment.author || t('canvasUI.comments.anonymous')
                        }}</span>
                        <span class="reply-time text-xs">{{ formatTime(comment.createdAt) }}</span>
                        <button
                          v-if="canDeleteComment(thread, comment)"
                          class="delete-reply-button ml-auto p-1 rounded border-0 bg-transparent cursor-pointer opacity-0 transition-all duration-200"
                          :title="t('canvasUI.comments.deleteReply')"
                          @click="deleteComment(thread.id, comment.id)"
                        >
                          <CIcon icon="times" :size="12" />
                        </button>
                      </div>
                      <div class="reply-text text-xs leading-relaxed">{{ comment.content }}</div>
                    </div>
                  </div>
                </div>

                <!-- Reply composer -->
                <div v-if="!thread.resolved" class="thread-reply-section mt-1">
                  <div
                    v-if="!showReplyBox[thread.id]"
                    class="reply-trigger inline-flex items-center gap-1.5 text-xs font-medium cursor-pointer py-1 transition-colors duration-200"
                    @click="showReplyBox[thread.id] = true"
                  >
                    <CIcon icon="reply" :size="12" />
                    {{ t('canvasUI.comments.reply') }}
                  </div>

                  <div v-else class="reply-composer mt-2">
                    <div class="relative">
                      <textarea
                        v-model="replyTexts[thread.id]"
                        class="reply-input w-full px-3 py-2.5 border rounded-lg text-sm resize-none transition-all duration-200"
                        :data-thread-id="thread.id"
                        :placeholder="t('canvasUI.comments.writeReply')"
                        rows="2"
                        @input="emit('user-activity')"
                        @keydown.enter.prevent="handleReply(thread.id)"
                        @keydown.escape="cancelReply(thread.id)"
                      />
                    </div>
                    <div class="reply-actions flex justify-end gap-2 mt-0">
                      <button
                        class="cancel-btn px-3.5 py-1.5 border-0 rounded-md text-xs font-medium cursor-pointer transition-all duration-200"
                        @click="cancelReply(thread.id)"
                      >
                        {{ t('canvasUI.comments.cancel') }}
                      </button>
                      <button
                        class="send-btn px-3.5 py-1.5 border-0 rounded-md text-xs font-medium cursor-pointer transition-all duration-200 text-white"
                        :disabled="!replyTexts[thread.id]?.trim()"
                        @click="handleReply(thread.id)"
                      >
                        {{ t('canvasUI.comments.send') }}
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              <div class="thread-actions flex gap-2 opacity-100">
                <button
                  v-if="!thread.resolved"
                  class="action-button w-8 h-8 border-0 rounded-full flex items-center justify-center cursor-pointer transition-all duration-200 p-0 m-0"
                  :title="t('canvasUI.comments.resolveThread')"
                  @click="resolveThread(thread.id)"
                >
                  <CIcon icon="check-circle" :size="12" />
                </button>
                <button
                  v-if="!thread.resolved"
                  class="action-button w-8 h-8 border-0 rounded-full flex items-center justify-center cursor-pointer transition-all duration-200 p-0 m-0"
                  :title="t('canvasUI.comments.assignUser')"
                  @click="openAssignmentModal(thread)"
                >
                  <CIcon icon="user-plus" :size="12" />
                </button>
                <button
                  v-if="canDeleteThread(thread)"
                  class="action-button delete-button w-8 h-8 border-0 rounded-full flex items-center justify-center cursor-pointer transition-all duration-200 p-0 m-0"
                  :title="t('canvasUI.comments.deleteThread')"
                  @click="deleteThread(thread.id)"
                >
                  <CIcon icon="trash" :size="12" />
                </button>
              </div>
            </div>
          </div>
        </transition-group>
      </div>

      <!-- New thread composer -->
      <div class="comment-composer flex gap-3 p-4 border-t bg-white">
        <div class="composer-avatar flex-shrink-0">
          <div
            class="avatar-placeholder w-10 h-10 rounded-full flex items-center justify-center text-sm font-semibold text-white"
          >
            {{ getInitials(props.currentUser?.name || currentUserData.name) }}
          </div>
        </div>
        <div class="composer-body flex-1">
          <div
            v-if="!isComposing"
            class="composer-trigger px-4 py-2.5 bg-white border rounded-lg cursor-pointer transition-all duration-200"
            @click="handleComposerOpen"
          >
            <span class="text-sm">{{ t('canvasUI.comments.addComment') }}</span>
          </div>
          <div v-else class="composer-form bg-white border rounded-lg overflow-hidden">
            <div class="relative">
              <textarea
                ref="composerInput"
                v-model="newThreadText"
                class="composer-input w-full px-4 py-3 border-0 text-sm resize-none focus:outline-none"
                :placeholder="t('canvasUI.comments.addComment')"
                rows="3"
                @input="emit('user-activity')"
                @keydown.enter.ctrl="createThread()"
                @keydown.escape="cancelCompose"
              />
            </div>
            <div class="composer-footer flex justify-between items-center px-4 py-2 border-t">
              <div class="composer-hint text-xs">{{ t('canvasUI.comments.ctrlEnterToSend') }}</div>
              <div class="composer-actions flex gap-2">
                <button
                  class="cancel-btn px-3.5 py-1.5 border-0 rounded-md text-xs font-medium cursor-pointer transition-all duration-200"
                  @click="cancelCompose"
                >
                  {{ t('canvasUI.comments.cancel') }}
                </button>
                <button
                  class="send-btn px-3.5 py-1.5 border-0 rounded-md text-xs font-medium cursor-pointer transition-all duration-200 text-white"
                  :disabled="!newThreadText.trim()"
                  @click="createThread"
                >
                  {{ t('canvasUI.comments.send') }}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Assignment modal -->
    <Teleport to="body">
      <div
        v-if="showAssignmentModal"
        class="assignment-modal-overlay fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[10000]"
        @click="showAssignmentModal = false"
      >
        <div class="assignment-modal bg-white rounded-xl shadow-2xl w-[70%] max-h-[500px] flex flex-col" @click.stop>
          <div class="assignment-modal-header flex justify-between items-center p-3 border-b">
            <h3 class="m-0 text-base font-semibold">{{ t('canvasUI.comments.assignUserToThread') }}</h3>
            <button
              class="assignment-close-btn w-8 h-8 border-0 rounded-lg text-xs cursor-pointer flex items-center justify-center transition-all duration-200 p-0 m-0"
              @click="showAssignmentModal = false"
            >
              <CIcon icon="times" :size="14" />
            </button>
          </div>
          <div class="assignment-modal-list flex-1 overflow-y-auto py-2 pr-8 pl-2">
            <div
              v-for="user in users"
              :key="user.value"
              class="assignment-modal-item flex items-center gap-3 p-2 rounded-lg cursor-pointer transition-all duration-200"
              :class="{ selected: currentAssigningThread?.assignee?.value === user.value }"
              @click="handleAssignUser(user)"
            >
              <div
                class="assignment-modal-avatar w-9 h-9 rounded-full flex items-center justify-center text-sm font-semibold flex-shrink-0 text-white"
              >
                {{ getInitials(user.name) }}
              </div>
              <div class="assignment-modal-info flex-1 min-w-0">
                <div class="assignment-modal-name text-sm font-medium whitespace-nowrap overflow-hidden text-ellipsis">
                  {{ user.name }}
                </div>
                <div class="assignment-modal-email text-xs whitespace-nowrap overflow-hidden text-ellipsis mt-0">
                  {{ user.email }}
                </div>
              </div>
            </div>
            <button
              v-if="currentAssigningThread?.assignee"
              class="clear-assignment-btn w-[calc(100%-16px)] m-2 px-4 py-2.5 border-0 rounded-lg text-sm font-medium cursor-pointer transition-all duration-200"
              @click="handleAssignUser(null)"
            >
              {{ t('canvasUI.comments.clearAssignment') }}
            </button>
          </div>
        </div>
      </div>
    </Teleport>
  </div>
</template>

<script setup lang="ts">
import { ref, reactive, computed, nextTick, onMounted, onBeforeUnmount, toRef } from 'vue'
import type { User } from '@openapi'
import { usePitcherApi, useAdmin } from '@lib/sdk/main'
import { useSharedCommentsStorage } from '@lib/composables/useSharedCommentsStorage'
import { useCommentTracking } from '@lib/composables/useCommentTracking'
import { useI18n } from 'vue-i18n'
import CIcon from '@lib/components/CIcon/CIcon.vue'
import useThemeVars from '@lib/composables/useThemeVars'

// Define Owner type locally
type Owner = {
  value: string | number
  name: string
  email: string
}

interface Props {
  componentId?: string
  currentUser?: User | null
  canvasId?: string
}

const props = defineProps<Props>()
const emit = defineEmits<{
  'user-activity': []
}>()
const { t } = useI18n()

const themeVars = useThemeVars()

interface Thread {
  id: string
  content: string
  author: string
  authorId?: string | number
  authorEmail?: string
  createdAt: string
  resolved: boolean
  canvasId?: string
  componentId?: string
  assignee?: Owner | null
  comments: Array<{
    id: string
    content: string
    author?: string
    authorId?: string | number
    authorEmail?: string
    createdAt: string
  }>
}

// Use shared backend storage for threads
const canvasIdRef = toRef(props, 'canvasId')
const { threads, isLoading, isSaving, lastSaved, saveComments } = useSharedCommentsStorage(canvasIdRef)

// Initialize comment tracking
const { trackCommentAdded, trackCommentReplied, trackCommentAssigned, trackCommentResolved, trackCommentDeleted } =
  useCommentTracking()

// Track new replies for notifications
const newReplyNotification = ref<string | null>(null)
const showNotification = (message: string) => {
  newReplyNotification.value = message
  setTimeout(() => {
    newReplyNotification.value = null
  }, 3000)
}

// Current user data
const currentUserData = ref<{
  id: string
  name: string
  email?: string
}>({
  id: 'anonymous',
  name: 'Anonymous User',
})
const newThreadText = ref('')
const replyTexts = reactive<Record<string, string>>({})
const showReplyBox = reactive<Record<string, boolean>>({})
const isComposing = ref(false)
const composerInput = ref<HTMLTextAreaElement>()

// User management
const users = ref<Owner[]>([])
const isLoadingUsers = ref(false)
const showAssignmentModal = ref(false)
const currentAssigningThread = ref<Thread | null>(null)

// Open assignment modal
const openAssignmentModal = (thread: Thread) => {
  currentAssigningThread.value = thread
  showAssignmentModal.value = true
}

// Handle user assignment from modal
const handleAssignUser = (user: Owner | null) => {
  if (currentAssigningThread.value) {
    assignUser(currentAssigningThread.value.id, user)
    showAssignmentModal.value = false
    currentAssigningThread.value = null
  }
}

// Filter threads for current component
const localThreads = computed(() => {
  return threads.value
    .filter((thread) => !props.componentId || thread.componentId === props.componentId)
    .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
})

const resolvedCount = computed(() => {
  return localThreads.value.filter((thread) => thread.resolved).length
})

const getInitials = (name: string) => {
  if (!name) return '?'
  const parts = name.split(' ')
  if (parts.length >= 2) {
    return parts[0][0].toUpperCase() + parts[1][0].toUpperCase()
  }
  return name.substring(0, 2).toUpperCase()
}

// Fetch current user data
const fetchCurrentUser = async () => {
  try {
    let api: any = usePitcherApi()
    if (window.location.href.indexOf('/admin/') !== -1) {
      api = useAdmin()
    }
    const env = await api.getEnv()

    // Use the actual logged-in user information
    currentUserData.value = {
      id: String(env.pitcher.user.id),
      name: env.pitcher.user.name || env.pitcher.user.email || 'Anonymous User',
      email: env.pitcher.user.email,
    }
  } catch (userInfoError) {
    console.warn('SimpleComments: Failed to get user info, using fallback', userInfoError)
    // Use fallback user info if we can't get it from Pitcher
    currentUserData.value = {
      id: `user-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`,
      name: 'Anonymous User',
    }
  }
}

const formatTime = (timestamp: string) => {
  const date = new Date(timestamp)
  const now = new Date()
  const diff = now.getTime() - date.getTime()

  if (diff < 60000) return t('canvasUI.comments.justNow')
  if (diff < 3600000) return t('canvasUI.comments.minutesAgo', { minutes: Math.floor(diff / 60000) })
  if (diff < 86400000) return t('canvasUI.comments.hoursAgo', { hours: Math.floor(diff / 3600000) })
  return date.toLocaleDateString()
}

const createThread = () => {
  if (!newThreadText.value.trim()) return

  const newThread: Thread = {
    id: `thread_${Date.now()}`,
    content: newThreadText.value.trim(),
    author: props.currentUser?.name || currentUserData.value.name,
    authorId: props.currentUser?.id || currentUserData.value.id,
    authorEmail: props.currentUser?.email || currentUserData.value.email,
    createdAt: new Date().toISOString(),
    resolved: false,
    canvasId: props.canvasId,
    componentId: props.componentId,
    assignee: null,
    comments: [],
  }

  threads.value.unshift(newThread)
  newThreadText.value = ''
  isComposing.value = false
  // Track comment added event
  trackCommentAdded({
    canvasId: props.canvasId,
    componentId: props.componentId,
    threadId: newThread.id,
    content: newThread.content,
    authorId: newThread.authorId,
    authorName: newThread.author,
    authorEmail: newThread.authorEmail,
  })

  // Show notification
  showNotification(t('canvasUI.comments.commentCreated'))

  // Save to backend
  saveComments()
}

const handleReply = (threadId: string) => {
  const replyText = replyTexts[threadId]
  if (!replyText?.trim()) return

  const thread = threads.value.find((t) => t.id === threadId)
  if (!thread) return

  const newComment = {
    id: `comment_${Date.now()}`,
    content: replyText.trim(),
    author: props.currentUser?.name || currentUserData.value.name,
    authorId: props.currentUser?.id || currentUserData.value.id,
    authorEmail: props.currentUser?.email || currentUserData.value.email,
    createdAt: new Date().toISOString(),
  }

  thread.comments.push(newComment)

  replyTexts[threadId] = ''
  showReplyBox[threadId] = false

  // Track comment replied event
  trackCommentReplied({
    canvasId: props.canvasId,
    componentId: props.componentId,
    threadId: thread.id,
    commentId: newComment.id,
    content: newComment.content,
    authorId: newComment.authorId,
    authorName: newComment.author,
    authorEmail: newComment.authorEmail,
  })

  // Show notification
  showNotification(t('canvasUI.comments.replyAdded', { name: props.currentUser?.name || currentUserData.value.name }))

  // Save to backend
  saveComments()
}

const cancelReply = (threadId: string) => {
  replyTexts[threadId] = ''
  showReplyBox[threadId] = false
}

const cancelCompose = () => {
  newThreadText.value = ''
  isComposing.value = false
}

const resolveThread = (threadId: string) => {
  const thread = threads.value.find((t) => t.id === threadId)
  if (thread) {
    thread.resolved = true

    // Track comment resolved event
    trackCommentResolved({
      canvasId: props.canvasId,
      componentId: props.componentId,
      threadId: thread.id,
      authorId: thread.authorId,
      authorName: thread.author,
      authorEmail: thread.authorEmail,
    })

    saveComments()
  }
}

// Focus composer when opened
const handleComposerOpen = async () => {
  isComposing.value = true
  await nextTick()
  composerInput.value?.focus()
}

// Check if user can delete thread
const canDeleteThread = (thread: Thread) => {
  // User can delete if they are the author
  const userId = props.currentUser?.id || currentUserData.value.id
  return thread.authorId === userId || String(thread.authorId) === String(userId)
}

// Check if user can delete comment
const canDeleteComment = (thread: Thread, comment: any) => {
  // User can delete if they are the comment author or thread author
  const userId = props.currentUser?.id || currentUserData.value.id
  return (
    comment.authorId === userId ||
    String(comment.authorId) === String(userId) ||
    thread.authorId === userId ||
    String(thread.authorId) === String(userId)
  )
}

// Delete thread
const deleteThread = (threadId: string) => {
  if (confirm(t('canvasUI.comments.confirmDeleteThread'))) {
    const thread = threads.value.find((t) => t.id === threadId)
    if (thread) {
      const index = threads.value.findIndex((t) => t.id === threadId)
      if (index !== -1) {
        threads.value.splice(index, 1)

        // Track comment deleted event
        trackCommentDeleted({
          canvasId: props.canvasId,
          componentId: props.componentId,
          threadId: thread.id,
          authorId: thread.authorId,
          authorName: thread.author,
          authorEmail: thread.authorEmail,
        })

        showNotification(t('canvasUI.comments.threadDeleted'))
        saveComments()
      }
    }
  }
}

// Delete comment
const deleteComment = (threadId: string, commentId: string) => {
  if (confirm(t('canvasUI.comments.confirmDeleteReply'))) {
    const thread = threads.value.find((t) => t.id === threadId)
    if (thread) {
      const comment = thread.comments.find((c) => c.id === commentId)
      const index = thread.comments.findIndex((c) => c.id === commentId)
      if (index !== -1 && comment) {
        thread.comments.splice(index, 1)

        // Track comment deleted event (for reply)
        trackCommentDeleted({
          canvasId: props.canvasId,
          componentId: props.componentId,
          threadId: thread.id,
          commentId: comment.id,
          authorId: comment.authorId,
          authorName: comment.author,
          authorEmail: comment.authorEmail,
        })

        showNotification(t('canvasUI.comments.replyDeleted'))
        saveComments()
      }
    }
  }
}

// User assignment
const assignUser = (threadId: string, user: Owner | null) => {
  const thread = threads.value.find((t) => t.id === threadId)
  if (thread) {
    thread.assignee = user

    // Track comment assigned event
    trackCommentAssigned({
      canvasId: props.canvasId,
      componentId: props.componentId,
      threadId: thread.id,
      authorId: thread.authorId,
      authorName: thread.author,
      authorEmail: thread.authorEmail,
      assigneeId: user?.value,
      assigneeName: user?.name,
      assigneeEmail: user?.email,
    })

    saveComments()
  }
}

// This is handled in the main onMounted below

// Close modals on escape key
const handleEscapeKey = (event: KeyboardEvent) => {
  if (event.key === 'Escape') {
    showAssignmentModal.value = false
  }
}

// Fetch users - using mock data for now
const fetchUsersFromApi = async () => {
  try {
    let api: any = usePitcherApi()
    if (window.location.href.indexOf('/admin/') !== -1) {
      api = useAdmin()
    }

    const env = await api.getEnv()
    const apiResponse = await api.getUsers({ page_size: 500, instance_id: env.pitcher.instance.id })
    let users = []
    if (apiResponse.results) {
      users = apiResponse.results
    } else {
      users = apiResponse
    }
    return users
      .sort((a: any, b: any) => a.name.localeCompare(b.name))
      .map((user: any) => ({
        value: user.id,
        name: user.name,
        email: user.email,
      }))
  } catch (error) {
    console.error('Error fetching users:', error)
    return []
  }
}

onMounted(async () => {
  document.addEventListener('keydown', handleEscapeKey)

  // Fetch current user data
  await fetchCurrentUser()

  // Fetch users list
  isLoadingUsers.value = true
  try {
    users.value = await fetchUsersFromApi()
  } catch (error) {
    console.error('Failed to fetch users:', error)
  } finally {
    isLoadingUsers.value = false
  }
})

onBeforeUnmount(() => {
  document.removeEventListener('keydown', handleEscapeKey)
})
</script>

<style scoped lang="scss">
.comments-container {
  position: relative;
  display: flex;
  flex-direction: column;
  background: #ffffff;
  border-radius: 12px;
  height: 100%;
  max-height: 80vh;
  overflow: hidden;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
}

// Header
.comments-header {
  padding: 20px 24px;
  border-bottom: 1px solid v-bind('themeVars.primary6');
  background: #ffffff; //primary6
}

.header-content {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.header-title {
  margin: 0;
  font-size: 18px;
  font-weight: 600;
  color: #212121; //text
  display: flex;
  align-items: center;
  gap: 8px;
}

.header-icon {
  color: v-bind('themeVars.primary2');
}

.thread-stats {
  display: flex;
  gap: 8px;
}

.stat-badge {
  font-size: 10px;
  font-weight: 700;
  padding: 3px 9px;
  border-radius: 16px;
  background: v-bind('themeVars.primary6_1');
  color: v-bind('themeVars.primary');

  &.resolved {
    background: #f9ffe9; //success light
    color: #57a40f; //success dark
  }

  &.saving,
  &.saved {
    background: #fffdea; //warning light
    color: #846a00; //warning dark
    display: inline-flex;
    align-items: center;
    gap: 4px;
  }

  &.saved {
    background: #57a40f; //success dark
    color: #f9ffe9; //success light
  }
}

.saving-icon {
  animation: spin 1s linear infinite;
}

@keyframes spin {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}

// Notification banner
.notification-banner {
  position: absolute;
  top: 16px;
  left: 50%;
  transform: translateX(-50%);
  background: v-bind('themeVars.primary6');
  color: #212121; //text
  border: 1px solid v-bind('themeVars.primary5');
  padding: 8px 16px;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 500;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
  z-index: 1001;
}

.notification-enter-active,
.notification-leave-active {
  transition: all 0.3s ease;
}

.notification-enter-from {
  opacity: 0;
  transform: translate(-50%, -20px);
}

.notification-leave-to {
  opacity: 0;
  transform: translate(-50%, -10px);
}

// Main wrapper
.comments-wrapper {
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

// Loading state
.loading-state {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 48px;
  text-align: center;
}

.loading-spinner {
  color: v-bind('themeVars.primary');
  margin-bottom: 16px;
}

.loading-text {
  margin: 0;
  font-size: 16px;
  font-weight: 500;
  color: v-bind('themeVars.primary2');
}

// Empty state
.empty-state {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 48px;
  text-align: center;
}

.empty-icon {
  color: v-bind('themeVars.primary3');
  margin-bottom: 16px;
}

.empty-text {
  margin: 0;
  font-size: 16px;
  font-weight: 500;
  color: v-bind('themeVars.primary3');
}

.empty-subtext {
  margin: 0;
  font-size: 14px;
  color: v-bind('themeVars.primary3');
}

// Thread list
.comments-threads {
  flex: 1;
  padding: 16px;
  overflow-y: auto;
  scrollbar-width: thin;
  scrollbar-color: #cbd5e1 transparent;

  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-track {
    background: transparent;
  }

  &::-webkit-scrollbar-thumb {
    background: #cbd5e1;
    border-radius: 3px;
  }
}

// Animations
.thread-list-enter-active,
.thread-list-leave-active {
  transition: all 0.3s ease;
}

.thread-list-enter-from {
  opacity: 0;
  transform: translateY(-10px);
}

.thread-list-leave-to {
  opacity: 0;
  transform: translateX(20px);
}

// Thread styles
.comment-thread {
  padding: 0;
  margin-bottom: 16px;
  transition: all 0.2s ease;

  &.thread-resolved {
    opacity: 0.7;

    .comment-content,
    .reply-text {
      color: v-bind('themeVars.primary4');
    }
  }
}

.thread-main {
  display: flex;
  gap: 12px;
}

.thread-avatar,
.composer-avatar {
  flex-shrink: 0;
}

.avatar-placeholder {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: v-bind('themeVars.primary');
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  font-weight: 600;

  &.small {
    width: 24px;
    height: 24px;
    font-size: 10px;
  }
}

.thread-body {
  flex: 1;
  min-width: 0;
}

.thread-meta {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 2px;
}

.author-name {
  font-weight: 600;
  color: #212121; //text
  font-size: 14px;
}

.thread-time,
.reply-time {
  font-size: 12px;
  color: v-bind('themeVars.primary3');
}

.resolved-badge {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 12px;
  font-weight: 500;
  color: #f9ffe9; //success light
  background: #57a40f; //success dark
  padding: 2px 8px;
  border-radius: 12px;
}

.comment-content {
  color: #212121; //text
  line-height: 1.6;
  font-size: 14px;
  word-wrap: break-word;
}

// Thread actions
.thread-actions {
  display: flex;
  gap: 8px;
  opacity: 1;
}

.action-button {
  width: 32px;
  height: 32px;
  border: none;
  background: v-bind('themeVars.primary6');
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s;
  color: v-bind('themeVars.primary3');
  padding: 0;
  margin: 0;

  &:hover {
    background: v-bind('themeVars.primary5');
    color: v-bind('themeVars.primary');
  }

  &.delete-button {
    &:hover {
      background: #fef7f5; //danger light
      color: #bc1637; //danger dark
    }
  }
}

.delete-reply-button {
  margin-left: auto;
  padding: 4px;
  border: none;
  background: transparent;
  border-radius: 4px;
  cursor: pointer;
  color: #94a3b8;
  opacity: 0;
  transition: all 0.2s;

  &:hover {
    background: #fef7f5; //danger light
    color: #bc1637; //danger dark
  }
}

.reply:hover .delete-reply-button {
  opacity: 1;
}

// Replies
.thread-replies {
  margin-top: 12px;
  margin-left: 28px;
  padding-left: 12px;
  border-left: 2px solid v-bind('themeVars.primary5');
}

.reply {
  display: flex;
  gap: 8px;
  margin-bottom: 12px;

  &:last-child {
    margin-bottom: 0;
  }
}

.reply-avatar {
  flex-shrink: 0;
}

.reply-content {
  flex: 1;
}

.reply-meta {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-bottom: 0px;
}

.reply-author {
  font-size: 12px;
  font-weight: 500;
  color: #212121; //text
}

.reply-text {
  font-size: 12px;
  color: v-bind('themeVars.primary2');
  line-height: 1.5;
}

// Reply section
.thread-reply-section {
  margin-top: 4px;
}

.reply-trigger {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  font-weight: 500;
  color: v-bind('themeVars.primary');
  cursor: pointer;
  padding: 4px 0;
  transition: color 0.2s;

  &:hover {
    color: v-bind('themeVars.primary');
  }

  svg {
    opacity: 0.8;
  }
}

.reply-composer {
  margin-top: 8px;
}

.reply-input {
  width: 100%;
  padding: 10px 12px;
  border: 1px solid v-bind('themeVars.primary5');
  border-radius: 8px;
  font-size: 14px;
  font-family: inherit;
  resize: none;
  transition: all 0.2s;

  &:focus {
    outline: none;
    border-color: v-bind('themeVars.primary4');
  }
}

.reply-actions {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  margin-top: 0px;
}

// Composer
.comment-composer {
  display: flex;
  gap: 12px;
  padding: 16px;
  border-top: 1px solid v-bind('themeVars.primary6');
  background: #fff; //primary6
}

.composer-body {
  flex: 1;
}

.composer-trigger {
  padding: 10px 16px;
  background: #ffffff;
  border: 1px solid v-bind('themeVars.primary5');
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s;

  span {
    color: v-bind('themeVars.primary4');
    font-size: 14px;
  }

  &:hover {
    border-color: v-bind('themeVars.primary4');
    background: #ffffff;
  }
}

.composer-form {
  background: #ffffff;
  border: 1px solid v-bind('themeVars.primary5');
  border-radius: 8px;
  overflow: hidden;
}

.composer-input {
  width: 100%;
  padding: 12px 16px;
  border: none;
  font-size: 14px;
  font-family: inherit;
  resize: none;

  &:focus {
    outline: none;
  }
}

.composer-footer {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 16px;
  background: v-bind('themeVars.primary6_1');
  border-top: 1px solid v-bind('themeVars.primary5');
}

.composer-hint {
  font-size: 12px;
  color: v-bind('themeVars.primary3');

  kbd {
    padding: 2px 6px;
    background: #ffffff;
    border: 1px solid v-bind('themeVars.primary5');
    border-radius: 4px;
    font-size: 11px;
    font-family: monospace;
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
  }
}

.composer-actions {
  display: flex;
  gap: 8px;
}

// Buttons
.cancel-btn,
.send-btn {
  padding: 6px 14px;
  border: none;
  border-radius: 6px;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
}

.cancel-btn {
  background: v-bind('themeVars.primary6');
  color: v-bind('themeVars.primary');

  &:hover {
    background: v-bind('themeVars.primary5');
    color: v-bind('themeVars.primary');
  }
}

.send-btn {
  background: v-bind('themeVars.primary');
  color: white;

  &:hover:not(:disabled) {
    opacity: 0.75;
  }

  &:disabled {
    opacity: 0.25;
    cursor: not-allowed;
  }
}

// Assignee badge and dropdown
.assignee-badge {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 12px;
  font-weight: 500;
  color: #5bbfe3; //info dark
  background: #f2fdff; //info light
  padding: 2px 8px;
  border-radius: 12px;
  cursor: default;
}

// Modal overlay styles
.assignment-modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10000;
  animation: fadeIn 0.2s ease;
}

@keyframes fadeIn {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

.assignment-modal {
  background: white;
  border-radius: 12px;
  box-shadow:
    0 20px 25px -5px rgba(0, 0, 0, 0.1),
    0 10px 10px -5px rgba(0, 0, 0, 0.04);

  width: 70%;
  max-height: 500px;
  display: flex;
  flex-direction: column;
  animation: slideIn 0.3s ease;
}

@keyframes slideIn {
  from {
    transform: translateY(20px);
    opacity: 0;
  }
  to {
    transform: translateY(0);
    opacity: 1;
  }
}

.assignment-modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px;
  border-bottom: 1px solid v-bind('themeVars.primary5');
}

.assignment-modal-header h3 {
  margin: 0;
  font-size: 16px;
  font-weight: 600;
  color: #212121; //text
}

.assignment-close-btn {
  width: 32px;
  height: 32px;
  border: none;
  background: v-bind('themeVars.primary6');
  border-radius: 8px;
  font-size: 12px;
  color: v-bind('themeVars.primary2');
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s;
  padding: 0;
  margin: 0;

  &:hover {
    background: v-bind('themeVars.primary5');
    color: v-bind('themeVars.primary');
  }
}

.assignment-modal-list {
  flex: 1;
  overflow-y: auto;
  padding: 8px 32px 8px 8px;
}

.assignment-modal-item {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 8px;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background: v-bind('themeVars.primary6');
  }

  &.selected {
    background: v-bind('themeVars.primary5');
  }
}

.assignment-modal-avatar {
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: v-bind('themeVars.primary');
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  font-weight: 600;
  flex-shrink: 0;
}

.assignment-modal-info {
  flex: 1;
  min-width: 0;
}

.assignment-modal-name {
  font-size: 14px;
  font-weight: 500;
  color: #212121; //text
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.assignment-modal-email {
  font-size: 13px;
  color: v-bind('themeVars.primary2');
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  margin-top: 0px;
}

.clear-assignment-btn {
  width: calc(100% - 16px);
  margin: 8px;
  padding: 10px 16px;
  border: none;
  background: v-bind('themeVars.primary6');
  color: v-bind('themeVars.primary2');
  font-size: 14px;
  font-weight: 500;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background: v-bind('themeVars.primary5');
    color: v-bind('themeVars.primary');
  }
}
</style>
